import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import type { AppLocale } from '../../../i18n';
import type { ItemPickerOption } from '../../shared/itemPickerModel';
import ItemGridPicker from '../../shared/ItemGridPicker';
import {
  compactSelectFieldSx,
  snapshotSelectFieldSx,
} from '../workbenchStyles';
import {
  filterItemOptionsByRecipeAvailability,
  filterRecipeOptionsByExclusion,
  type WorkbenchRecipeOption,
} from '../workbenchHelpers';
import { RecipeOptionLabel } from '../components/SelectOption';

export interface ProducedRecipeSelectorProps {
  locale: AppLocale;
  atlasIds?: string[];
  powerItemId?: string | null;
  itemOptions: ItemPickerOption[];
  recipeOptionsByItem: Record<string, WorkbenchRecipeOption[]>;
  selectedItemId: string;
  onSelectedItemChange: (itemId: string) => void;
  selectedRecipeId: string;
  onSelectedRecipeChange: (recipeId: string) => void;
  searchLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  recipeLabel: string;
  emptySelectionLabel: string;
  getExcludedRecipeIds?: (itemId: string) => string[];
  disabled?: boolean;
  /** Compact mode for sidebar use. */
  dense?: boolean;
}

export default function ProducedRecipeSelector({
  locale,
  atlasIds,
  powerItemId,
  itemOptions,
  recipeOptionsByItem,
  selectedItemId,
  onSelectedItemChange,
  selectedRecipeId,
  onSelectedRecipeChange,
  searchLabel,
  searchPlaceholder,
  emptyText,
  recipeLabel,
  emptySelectionLabel,
  getExcludedRecipeIds = () => [],
  disabled = false,
  dense = false,
}: ProducedRecipeSelectorProps) {
  const [itemQuery, setItemQuery] = useState('');

  const availableItemOptions = useMemo(
    () =>
      filterItemOptionsByRecipeAvailability(
        itemOptions,
        recipeOptionsByItem,
        getExcludedRecipeIds,
      ),
    [getExcludedRecipeIds, itemOptions, recipeOptionsByItem]
  );

  const recipeOptions = useMemo(
    () =>
      filterRecipeOptionsByExclusion(
        recipeOptionsByItem[selectedItemId],
        getExcludedRecipeIds(selectedItemId),
      ),
    [getExcludedRecipeIds, recipeOptionsByItem, selectedItemId]
  );

  const selectedItem = useMemo(
    () => itemOptions.find(item => item.itemId === selectedItemId) ?? null,
    [itemOptions, selectedItemId]
  );

  useEffect(() => {
    if (
      selectedItemId &&
      !availableItemOptions.some(item => item.itemId === selectedItemId)
    ) {
      onSelectedItemChange('');
    }
  }, [availableItemOptions, onSelectedItemChange, selectedItemId]);

  useEffect(() => {
    if (
      selectedRecipeId &&
      !recipeOptions.some(option => option.recipeId === selectedRecipeId)
    ) {
      onSelectedRecipeChange('');
    }
  }, [onSelectedRecipeChange, recipeOptions, selectedRecipeId]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: dense ? 0.5 : 1,
        flexWrap: 'wrap',
        alignItems: 'start',
        ...(dense ? {} : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' } }),
      }}
    >
      <Box sx={dense ? { flex: '1 1 0', minWidth: 0 } : undefined}>
        <ItemGridPicker
          items={availableItemOptions}
          selectedItemId={selectedItemId}
          query={itemQuery}
          onQueryChange={setItemQuery}
          onSelect={itemId => {
            if (itemId !== selectedItemId) {
              onSelectedRecipeChange('');
            }
            onSelectedItemChange(itemId);
          }}
          atlasIds={atlasIds}
          searchLabel={searchLabel}
          searchPlaceholder={searchPlaceholder}
          emptyText={emptyText}
          selectedItemName={selectedItem?.name}
          selectedItemIcon={selectedItem?.icon}
          disabled={disabled}
          dense={dense}
        />
      </Box>

      <Box sx={dense ? { flex: '1 1 0', minWidth: 0 } : undefined}>
        <TextField
          select
          fullWidth
          size="small"
          sx={dense ? snapshotSelectFieldSx : compactSelectFieldSx}
          value={selectedRecipeId}
          disabled={disabled || !selectedItemId || recipeOptions.length === 0}
          onChange={event => onSelectedRecipeChange(event.target.value)}
          slotProps={{
            htmlInput: {
              'aria-label': recipeLabel,
            },
          }}
        >
          <MenuItem value="">{emptySelectionLabel}</MenuItem>
          {recipeOptions.map(option => (
            <MenuItem key={option.recipeId} value={option.recipeId}>
              <RecipeOptionLabel
                recipeName={option.recipeName}
                inputs={option.inputs}
                outputs={option.outputs}
                cycleTimeSec={option.cycleTimeSec}
                locale={locale}
                atlasIds={atlasIds}
                highlightItemId={selectedItemId}
                powerItemId={powerItemId}
              />
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
}
